<template>
    <v-dialog v-model="dialog" max-width="800px" persistent scrollable>
        <v-card class="booking-dialog-card">
            <div class="booking-dialog-header pa-5">
                <div class="d-flex align-center justify-space-between">
                    <div class="booking-dialog-title">
                        <v-icon class="mr-2" color="white" size="20">mdi-email-send-outline</v-icon>
                        Send Organiser Payment Summary
                    </div>
                    <v-btn icon dark @click="closeDialog()">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>
            <v-card-text class="pa-6">
                <div class="booking-section mb-4">
                    <div class="booking-section-header mb-1">
                        <v-icon size="14" color="#3988AA" class="mr-1">mdi-format-title</v-icon>
                        SUBJECT
                    </div>
                    <v-text-field v-model="subject" outlined dense placeholder="Enter email subject"
                        hide-details></v-text-field>
                </div>
                <div class="booking-section">
                    <div class="booking-section-header mb-2">
                        <v-icon size="14" color="#3988AA" class="mr-1">mdi-text</v-icon>
                        EMAIL CONTENT
                    </div>
                    <wysiwyg v-model="body" placeholder="Compose the payment summary email..." />
                </div>
            </v-card-text>
            <v-divider />
            <v-card-actions class="pa-4 custom-buttons">
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog()">Cancel</v-btn>
                <v-btn dark color="#3988AA" :loading="sending" :disabled="!subject || !body" @click="sendEmail()">
                    <v-icon small left>mdi-send</v-icon>
                    Send
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from "vuex"
import { brandFor } from '@/utils/brand'

// my-stagit's "Send Invoice" (send_group_invoice) replacement: one email to the
// organiser listing everyone's package, amount paid and payment date, plus the
// total. Rows are built by ManageInvitees with the same helpers as its table.
export default {
    props: {
        show: {
            type: Boolean,
            default: false
        },
        lead: {
            type: Object,
            default: () => ({})
        },
        event: {
            type: Object,
            default: () => ({})
        },
        company_id: {
            type: Number,
            default: null
        },
        currency: {
            type: String,
            default: ''
        },
        // [{ name, role, package, amount, status, date }]
        rows: {
            type: Array,
            default: () => []
        }
    },
    data: () => ({
        subject: '',
        body: '',
        sending: false,
    }),
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user',
        }),
        dialog: {
            get() {
                return this.show
            },
            set(val) {
                if (!val) this.closeDialog()
            }
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.sending = false
                this.subject = `Payment summary for ${this.event?.name || ''}`
                this.body = this.buildDefaultBody()
            }
        }
    },
    methods: {
        closeDialog() {
            this.$emit('close')
        },
        // Names and package names are user-entered; keep them from breaking the HTML.
        escapeHtml(value) {
            return String(value ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
        },
        formatMoney(amount) {
            return `${this.escapeHtml(this.currency)}${Number(amount || 0).toFixed(2)}`
        },
        buildDefaultBody() {
            const lead = this.lead || {}
            const event = this.event || {}
            const advisorName = this.get_user?.name || ''
            const brandName = brandFor(event.company, lead.event_type || event.lead?.event_type).display_name || ''
            const payDate = event.pay_date ? this.$date(event.pay_date).format('DD/MM/YYYY') : ''
            const total = this.rows.reduce((sum, row) => sum + Number(row.amount || 0), 0)

            const cell = 'border:1px solid #DDDDDD;padding:6px 8px;color:black;font-size:14px;'
            const head = cell + 'background-color:#F2F2F2;font-weight:bold;text-align:left;'
            const rowsHtml = this.rows.map((row, index) => `
                    <tr>
                        <td style="${cell}">${index + 1}</td>
                        <td style="${cell}">${this.escapeHtml(row.name)}${row.role ? ` <i>(${this.escapeHtml(row.role)})</i>` : ''}</td>
                        <td style="${cell}">${this.escapeHtml(row.package || '—')}</td>
                        <td style="${cell}text-align:right;">${this.formatMoney(row.amount)}</td>
                        <td style="${cell}">${row.date ? this.$date(row.date).format('DD/MM/YYYY') : '—'}</td>
                        <td style="${cell}">${this.escapeHtml(row.status)}</td>
                    </tr>`).join('')

            return `
                <div style="text-align:center;font-size:28px;color:black;"><b>Hello ${this.escapeHtml(lead.first_name)},</b></div>
                <br>
                <p style="text-align:left;color:black;font-size:16px;">
                    Here is a summary of the payments made so far for <b>${this.escapeHtml(event.name)}</b>.
                </p>
                <table style="border-collapse:collapse;width:100%;">
                    <tr>
                        <th style="${head}">#</th>
                        <th style="${head}">Name</th>
                        <th style="${head}">Package</th>
                        <th style="${head}text-align:right;">Amount Paid</th>
                        <th style="${head}">Date Paid</th>
                        <th style="${head}">Status</th>
                    </tr>${rowsHtml}
                    <tr>
                        <td style="${cell}font-weight:bold;" colspan="3">Total Paid</td>
                        <td style="${cell}font-weight:bold;text-align:right;">${this.formatMoney(total)}</td>
                        <td style="${cell}" colspan="2"></td>
                    </tr>
                </table>
                <p style="text-align:left;color:black;font-size:16px;">
                    ${payDate ? `The balance for everyone is due by <b>${payDate}</b>.<br><br>` : ''}
                    If anything here doesn't look right, please get in touch.
                    <br><br>
                    Thank you,
                    <br>
                    ${this.escapeHtml(advisorName)}${brandName ? ` - ${this.escapeHtml(brandName)}` : ''}
                </p>
            `
        },
        async sendEmail() {
            this.sending = true
            try {
                const payload = {
                    company_id: this.company_id,
                    lead: this.lead,
                    event: this.event,
                    subject: this.subject,
                    body: this.body,
                    email_type: 'organiser_payment_summary',
                }
                const result = await (await this.$axios.post('admin/events/send_organiser_email', payload)).data
                if (result.response == true) {
                    this.$toast.success(result.message)
                    this.closeDialog()
                } else {
                    this.$toast.error('Failed to send payment summary email')
                }
            } catch (error) {
                this.$toast.error('Failed to send payment summary email')
            } finally {
                this.sending = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.booking-dialog-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.booking-dialog-title {
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    color: white;
    display: flex;
    align-items: center;
}

.booking-section-header {
    font-family: Manrope;
    font-size: 12px;
    font-weight: 700;
    color: #3D3D3D;
    display: flex;
    align-items: center;
}
</style>
